import React, { memo } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ReferenceArea
} from 'recharts'
import { ChartWidgetConfig } from '../../../types'

interface DataPoint {
  timestamp: number
  [key: string]: number | string
}

interface DataStream {
  id: string
  name: string
  data: Array<{ timestamp: number; value: number }>
  color?: string
  unit?: string
}

interface SimpleChartProps {
  dataStreams: DataStream[]
  config: ChartWidgetConfig
  width?: number | string
  height?: number | string
  showGrid?: boolean
  onTimeRangeChange?: (timeRange: { start: Date; end: Date }) => void
  enableZoom?: boolean
  hiddenSeries?: Set<string>
  onSeriesToggle?: (seriesId: string) => void
}

export const SimpleChart: React.FC<SimpleChartProps> = memo(({
  dataStreams,
  config,
  width = '100%',
  height = 300,
  showGrid = true,
  onTimeRangeChange,
  enableZoom = true,
  hiddenSeries: externalHiddenSeries,
  onSeriesToggle
}) => {
  // Zoom state (ReferenceArea-based)
  const [refAreaLeft, setRefAreaLeft] = React.useState<string>('')
  const [refAreaRight, setRefAreaRight] = React.useState<string>('')
  // Series visibility state - use external state if provided
  const [internalHiddenSeries, setInternalHiddenSeries] = React.useState<Set<string>>(new Set())
  const hiddenSeries = externalHiddenSeries || internalHiddenSeries
  // Combine all data streams into a single dataset for Recharts
  const chartData = React.useMemo(() => {
    if (!dataStreams.length) return []

    // Create maps for fast lookup - O(1) instead of O(n)
    const streamMaps = dataStreams.map(stream => {
      const dataMap = new Map<number, number | null>()
      stream.data.forEach(point => {
        dataMap.set(point.timestamp, point.value)
      })
      return { id: stream.id, dataMap }
    })

    // Get all unique timestamps
    const allTimestamps = new Set<number>()
    dataStreams.forEach(stream => {
      stream.data.forEach(point => allTimestamps.add(point.timestamp))
    })

    // Create combined data points with fast lookup
    const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => a - b)
    
    return sortedTimestamps.map(timestamp => {
      const dataPoint: DataPoint = { timestamp }
      
      streamMaps.forEach(({ id, dataMap }) => {
        dataPoint[id] = dataMap.get(timestamp) ?? null
      })
      
      return dataPoint
    })
  }, [dataStreams])

  // Custom tooltip formatter
  const customTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const date = new Date(label)
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '8px 12px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>
            {date.toLocaleString()}
          </div>
          {payload.map((entry: any, index: number) => {
            const stream = dataStreams.find(s => s.id === entry.dataKey)
            return (
              <div key={index} style={{ color: entry.color }}>
                {stream?.name || entry.dataKey}: {entry.value}
                {stream?.unit ? ` ${stream.unit}` : ''}
              </div>
            )
          })}
        </div>
      )
    }
    return null
  }

  // Format timestamp for X-axis
  const formatXAxis = (timestamp: number) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  // ReferenceArea-based zoom handlers (following Recharts example)
  const zoom = React.useCallback(() => {
    if (!enableZoom || !onTimeRangeChange || !refAreaLeft || !refAreaRight || refAreaLeft === refAreaRight) {
      setRefAreaLeft('')
      setRefAreaRight('')
      return
    }

    // Convert string labels to timestamps
    let leftTime = parseInt(refAreaLeft)
    let rightTime = parseInt(refAreaRight)
    
    // Ensure left is smaller than right
    if (leftTime > rightTime) [leftTime, rightTime] = [rightTime, leftTime]
    
    
    // Update dashboard time range (Grafana-style)
    onTimeRangeChange({
      start: new Date(leftTime),
      end: new Date(rightTime)
    })
    
    // Clear selection area
    setRefAreaLeft('')
    setRefAreaRight('')
  }, [enableZoom, onTimeRangeChange, refAreaLeft, refAreaRight, chartData.length])

  // Mouse handlers for selection
  const handleMouseDown = React.useCallback((e: any) => {
    if (!enableZoom || !e?.activeLabel) return
    setRefAreaLeft(e.activeLabel.toString())
  }, [enableZoom])

  const handleMouseMove = React.useCallback((e: any) => {
    if (!enableZoom || !refAreaLeft || !e?.activeLabel) return
    setRefAreaRight(e.activeLabel.toString())
  }, [enableZoom, refAreaLeft])

  const handleMouseUp = React.useCallback(() => {
    if (!enableZoom) return
    zoom()
  }, [enableZoom, zoom])

  // Legend click handler to toggle series visibility
  const handleLegendClick = React.useCallback((seriesId: string) => {
    if (onSeriesToggle) {
      onSeriesToggle(seriesId)
    } else {
      setInternalHiddenSeries(prev => {
        const newSet = new Set(prev)
        if (newSet.has(seriesId)) {
          newSet.delete(seriesId)
        } else {
          newSet.add(seriesId)
        }
        return newSet
      })
    }
  }, [onSeriesToggle])

  // Render different chart types
  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    }

    switch (config.chartType) {
      case 'area':
        return (
          <AreaChart 
            {...commonProps}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove} 
            onMouseUp={handleMouseUp}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={showGrid ? '#e0e0e0' : 'transparent'} />
            <XAxis 
              allowDataOverflow
              dataKey="timestamp" 
              tickFormatter={formatXAxis}
              stroke="#666"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
            />
            <YAxis 
              yAxisId="left" 
              allowDataOverflow 
              stroke="#666"
              label={{ 
                value: config.yAxes?.[0]?.unit 
                  ? `${config.yAxes[0].label || 'Value'} (${config.yAxes[0].unit})`
                  : config.yAxes?.[0]?.label || '', 
                angle: -90, 
                position: 'insideLeft' 
              }}
            />
            <YAxis 
              yAxisId="right" 
              orientation="right" 
              allowDataOverflow 
              stroke="#666"
              label={{ 
                value: config.yAxes?.[1]?.unit 
                  ? `${config.yAxes[1].label || 'Value'} (${config.yAxes[1].unit})`
                  : config.yAxes?.[1]?.label || '', 
                angle: 90, 
                position: 'insideRight' 
              }}
            />
            <Tooltip content={customTooltip} />
            {refAreaLeft && refAreaRight && (
              <ReferenceArea 
                yAxisId="left"
                x1={refAreaLeft} 
                x2={refAreaRight} 
                strokeOpacity={0.3}
              />
            )}

            {dataStreams.map((stream, index) => {
              const isHidden = hiddenSeries.has(stream.id)
              // Determine which Y-axis this stream should use (default to left)
              const yAxisId = (stream as any).yAxis || 'left'
              // Use individual stream color or fallback to config colors
              const streamColor = (stream as any).color || config.colors[index % config.colors.length]
              
              return (
                <Area
                  key={stream.id}
                  type="monotone"
                  dataKey={stream.id}
                  name={stream.name}
                  stroke={streamColor}
                  fill={streamColor}
                  fillOpacity={0.3}
                  connectNulls={config.connectNulls}
                  dot={config.showDataPoints ? { r: 1 } : false}
                  hide={isHidden}
                  yAxisId={yAxisId}
                />
              )
            })}
          </AreaChart>
        )

      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke={showGrid ? '#e0e0e0' : 'transparent'} />
            <XAxis 
              dataKey="timestamp" 
              tickFormatter={formatXAxis}
              stroke="#666"
            />
            <YAxis yAxisId="left" stroke="#666" />
            <YAxis yAxisId="right" orientation="right" stroke="#666" />
            <Tooltip content={customTooltip} />
            {refAreaLeft && refAreaRight && (
              <ReferenceArea 
                yAxisId="left"
                x1={refAreaLeft} 
                x2={refAreaRight} 
                strokeOpacity={0.3}
              />
            )}

            {dataStreams.map((stream, index) => {
              const isHidden = hiddenSeries.has(stream.id)
              // Determine which Y-axis this stream should use (default to left)
              const yAxisId = (stream as any).yAxis || 'left'
              // Use individual stream color or fallback to config colors
              const streamColor = (stream as any).color || config.colors[index % config.colors.length]
              
              return (
                <Bar
                  key={stream.id}
                  dataKey={stream.id}
                  name={stream.name}
                  fill={streamColor}
                  hide={isHidden}
                  yAxisId={yAxisId}
                />
              )
            })}
          </BarChart>
        )

      default: // 'line'
        return (
          <LineChart 
            {...commonProps}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove} 
            onMouseUp={handleMouseUp}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={showGrid ? '#e0e0e0' : 'transparent'} />
            <XAxis 
              allowDataOverflow
              dataKey="timestamp" 
              tickFormatter={formatXAxis}
              stroke="#666"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
            />
            <YAxis 
              yAxisId="left" 
              allowDataOverflow 
              stroke="#666"
              label={{ 
                value: config.yAxes?.[0]?.unit 
                  ? `${config.yAxes[0].label || 'Value'} (${config.yAxes[0].unit})`
                  : config.yAxes?.[0]?.label || '', 
                angle: -90, 
                position: 'insideLeft' 
              }}
            />
            <YAxis 
              yAxisId="right" 
              orientation="right" 
              allowDataOverflow 
              stroke="#666"
              label={{ 
                value: config.yAxes?.[1]?.unit 
                  ? `${config.yAxes[1].label || 'Value'} (${config.yAxes[1].unit})`
                  : config.yAxes?.[1]?.label || '', 
                angle: 90, 
                position: 'insideRight' 
              }}
            />
            <Tooltip content={customTooltip} />
            {refAreaLeft && refAreaRight && (
              <ReferenceArea 
                yAxisId="left"
                x1={refAreaLeft} 
                x2={refAreaRight} 
                strokeOpacity={0.3}
              />
            )}

            {dataStreams.map((stream, index) => {
              const isHidden = hiddenSeries.has(stream.id)
              // Determine which Y-axis this stream should use (default to left)
              const yAxisId = (stream as any).yAxis || 'left'
              // Use individual stream color or fallback to config colors
              const streamColor = (stream as any).color || config.colors[index % config.colors.length]
              
              // Y-axis assignment working correctly
              
              return (
                <Line
                  key={`${stream.id}_${index}`}
                  type="monotone"
                  dataKey={stream.id}
                  name={stream.name}
                  stroke={streamColor}
                  strokeWidth={config.strokeWidth || 2}
                  dot={config.showDataPoints ? { r: 1 } : false}
                  connectNulls={config.connectNulls}
                  hide={isHidden}
                  yAxisId={yAxisId}
                />
              )
            })}
          </LineChart>
        )
    }
  }

  if (!dataStreams.length) {
    return (
      <div style={{
        width,
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa',
        border: '2px dashed #dee2e6',
        borderRadius: '4px',
        color: '#6c757d'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>📊</div>
          <div>No data available</div>
          <div style={{ fontSize: '12px', marginTop: '4px' }}>
            Configure data queries to see chart
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Custom header legend - disabled when parent handles legend */}
      {config.legend.show && config.legend.position === 'top' && dataStreams.length > 0 && !onSeriesToggle && (
        <div style={{
          display: 'flex',
          gap: '12px',
          padding: '8px 16px',
          borderBottom: '1px solid #eee',
          flexWrap: 'wrap'
        }}>
          {dataStreams.map((stream, index) => (
            <div
              key={stream.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                opacity: hiddenSeries.has(stream.id) ? 0.5 : 1,
                cursor: 'pointer'
              }}
              onClick={() => handleLegendClick(stream.id)}
            >
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: config.colors[index % config.colors.length]
                }}
              />
              <span>{stream.name}</span>
            </div>
          ))}
        </div>
      )}
      
      {/* Chart */}
      <div style={{ flex: 1, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%" style={{ userSelect: 'none' }}>
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  )
}, (prevProps, nextProps) => {
  // Lightweight comparison for better performance
  return (
    prevProps.dataStreams.length === nextProps.dataStreams.length &&
    prevProps.dataStreams.every((stream, i) => 
      stream.id === nextProps.dataStreams[i]?.id &&
      stream.data.length === nextProps.dataStreams[i]?.data.length
    ) &&
    prevProps.config.chartType === nextProps.config.chartType &&
    prevProps.showGrid === nextProps.showGrid &&
    (prevProps.hiddenSeries?.size || 0) === (nextProps.hiddenSeries?.size || 0)
  )
})